import React, { useState, useEffect } from 'react';
import { X, Linkedin, AlertCircle, Check } from 'lucide-react';
import { initiateLinkedInLogin, isLinkedInAuthenticated } from '../lib/linkedin';

interface LinkedInProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LinkedInProfileModal: React.FC<LinkedInProfileModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsConnected(isLinkedInAuthenticated());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Initiate LinkedIn OAuth flow
      initiateLinkedInLogin();
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la connexion à LinkedIn');
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('linkedin_token');
    localStorage.removeItem('linkedin_token_expiry');
    setIsConnected(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-[#0A66C2] flex items-center">
            <Linkedin className="w-6 h-6 mr-2" />
            {isConnected ? 'Compte LinkedIn connecté' : 'Connexion LinkedIn'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 flex items-center text-sm">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        {isConnected ? (
          <div>
            <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
              <Check className="w-5 h-5 mr-2" />
              <span>Votre compte LinkedIn est connecté</span>
            </div>
            
            <p className="text-gray-600 mb-6">
              Vous pouvez maintenant partager vos offres d'emploi directement sur LinkedIn.
            </p>
            
            <button
              onClick={handleDisconnect}
              className="w-full border-2 border-red-500 text-red-500 py-3 px-4 rounded-md hover:bg-red-50 transition-colors"
            >
              Déconnecter mon compte LinkedIn
            </button>
          </div>
        ) : (
          <>
            <div className="text-gray-600 mb-6">
              <p className="mb-4">
                En connectant votre compte LinkedIn, vous autorisez Fantastic Job à :
              </p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Accéder à votre profil LinkedIn</li>
                <li>Publier des offres d'emploi en votre nom</li>
                <li>Partager du contenu sur votre fil d'actualité</li>
              </ul>
            </div>

            <button
              onClick={handleConnect}
              disabled={isLoading}
              className="w-full bg-[#0A66C2] text-white py-3 px-4 rounded-md hover:bg-[#004182] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Linkedin className="w-5 h-5" />
                  <span>Connecter mon compte LinkedIn</span>
                </>
              )}
            </button>
          </>
        )}

        <p className="text-xs text-gray-500 mt-4 text-center">
          En connectant votre compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
        </p>
      </div>
    </div>
  );
};

export default LinkedInProfileModal;